import React, { useState, useEffect, useRef } from "react";
import "./DemoVoiceAiAgent.css";
import { Building2, Heart, Scale, PhoneOff, MicOff, Volume2, Mic, Phone,PlayCircle ,Play} from 'lucide-react';
import Avatar from './Avatar.svg'


const CONVERSATION_TYPES = [
  {
    id: 'fintech',
    label: 'FinTech',
    icon: Building2,
    color: '#2563EB',
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    icon: Heart,
    color: '#2563EB',
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: Scale,
    color: '#2563EB',
  },
];

const MessageList = ({ messages, currentInterim }) => {
  const chatWindowRef = useRef(null);
  const lastMessageRef = useRef(null);

  useEffect(() => {
    if (messages.length > 1) {
        lastMessageRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
    }
}, [messages.length, currentInterim]);

  return (
      <div
          className="chat-window"
          ref={chatWindowRef}
      >
          {messages.map((message, index) => (
              <div
                  key={index}
                  className={`message ${message.sender === "agent" ? "agent" : "user"}`}
                  ref={index === messages.length - 1 ? lastMessageRef : null}
              >
                  <div className="agent-icon">
                      {message.sender === "agent" ? <img src={Avatar} alt="Agent" /> : "U"}
                  </div>
                  <div className="message-text">{message.text}</div>
              </div>
          ))}
          {currentInterim && (
              <div className="message user" ref={lastMessageRef}>
                  <div className="agent-icon">U</div>
                  <div className="message-text interim">{currentInterim}</div>
              </div>
          )}
      </div>
  );
};

const DemoVoiceAiAgent = () => {
  const [activeTab, setActiveTab] = useState("Healthcare");
  const [messages, setMessages] = useState(() => {
    const domainGreetings = {
        healthcare: {
            text: "Hello! I'm your medical insurance assistant. How can I help you today?"
        },
        fintech: {
            text: "Welcome! I'm your financial advisor AI assistant. How may I assist you today?"
        },
        legal: {
            text: "Hello! I'm your legal assistant AI. How can I help you with your legal matters today?"
        }
    };

    return [
        {
            sender: "agent",
            text: domainGreetings[activeTab.toLowerCase()].text
        }
    ];
});

const domainGreetings = {
    healthcare: {
        text: "Hello! I'm your medical insurance assistant. How can I help you today?"
    },
    fintech: {
        text: "Welcome! I'm your financial advisor AI assistant. How may I assist you today?"
    },
    legal: {
        text: "Hello! I'm your legal assistant AI. How can I help you with your legal matters today?"
    }
};
useEffect(() => {


    if (isConnected) {
        // If already connected, close existing connection
        handleHangUp();
        // Reconnect with new domain
        connectWebSocket();
    }

    setMessages([
        {
            sender: "agent",
            text: domainGreetings[activeTab.toLowerCase()].text
        }
    ]);
}, [activeTab]);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentInterim, setCurrentInterim] = useState("");
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  
  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const audioPlayerRef = useRef(new Audio());

  // Simple function to play audio from base64
  const playAudio = async (audioBase64) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(audioBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
      
      // Create URL and play
      const audioUrl = URL.createObjectURL(audioBlob);
      if (audioPlayerRef.current.src) {
        URL.revokeObjectURL(audioPlayerRef.current.src);
      }
      audioPlayerRef.current.src = audioUrl;
      await audioPlayerRef.current.play();
      
      // Cleanup URL after playing
      audioPlayerRef.current.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const startAudioStream = async () => {
    try {
      console.log('Requesting microphone access...');
      
      // First get the stream with more flexible constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: { ideal: 1 },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      mediaStreamRef.current = stream;
      
      // Create AudioContext first
      const context = new (window.AudioContext || window.webkitAudioContext)({
        // Let the browser choose the best sample rate
        latencyHint: 'interactive'
      });
      
      audioContextRef.current = context;
      
      // Get the actual sample rate from the context
      const contextSampleRate = context.sampleRate;
      console.log('Audio Context sample rate:', contextSampleRate);
      
      // Create and configure the source
      const source = context.createMediaStreamSource(stream);
      
      // Calculate buffer size based on context sample rate
      const bufferSize = 2048;
      processorRef.current = context.createScriptProcessor(bufferSize, 1, 1);
      
      processorRef.current.onaudioprocess = (e) => {
        if (wsRef.current?.readyState === WebSocket.OPEN && !isMuted) {
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Resample to 16kHz if needed
          let processedData;
          if (contextSampleRate !== 16000) {
            const downsamplingFactor = Math.floor(contextSampleRate / 16000);
            const targetLength = Math.floor(inputData.length / downsamplingFactor);
            processedData = new Float32Array(targetLength);
            
            for (let i = 0; i < targetLength; i++) {
              // Simple averaging for downsampling
              let sum = 0;
              for (let j = 0; j < downsamplingFactor; j++) {
                sum += inputData[i * downsamplingFactor + j] || 0;
              }
              processedData[i] = sum / downsamplingFactor;
            }
          } else {
            processedData = inputData;
          }
          
          // Convert to 16-bit PCM
          const int16Array = new Int16Array(processedData.length);
          for (let i = 0; i < processedData.length; i++) {
            const s = Math.max(-1, Math.min(1, processedData[i]));
            int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          try {
            wsRef.current.send(int16Array.buffer);
          } catch (error) {
            console.error('Error sending audio data:', error);
          }
        }
      };
      
      // Connect the nodes
      source.connect(processorRef.current);
      processorRef.current.connect(context.destination);
      
      console.log('Audio stream started successfully');
      
      // Resume the audio context (needed for some browsers)
      if (context.state === 'suspended') {
        await context.resume();
      }
      
    } catch (error) {
      console.error('Error in startAudioStream:', error);
      
      // Detailed error handling
      let errorMessage = 'An error occurred while setting up the audio stream.';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access was denied. Please allow microphone access to use this feature.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone was found. Please ensure you have a working microphone connected.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Security error occurred. Please ensure you\'re using HTTPS.';
      }
      
      alert(errorMessage);
      throw error; // Rethrow to handle it in the calling function
    }
  };
  
  // Enhanced cleanup function
  const cleanupAudioStream = () => {
    // Stop all media tracks
    if (mediaStreamRef.current) {
      const tracks = mediaStreamRef.current.getTracks();
      tracks.forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.warn('Error stopping track:', e);
        }
      });
      mediaStreamRef.current = null;
    }
    
    // Disconnect and cleanup processor
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
        processorRef.current = null;
      } catch (e) {
        console.warn('Error disconnecting processor:', e);
      }
    }
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
        audioContextRef.current = null;
      } catch (e) {
        console.warn('Error closing AudioContext:', e);
      }
    }
    
    // Cleanup audio player
    if (audioPlayerRef.current) {
      try {
        audioPlayerRef.current.pause();
        if (audioPlayerRef.current.src) {
          URL.revokeObjectURL(audioPlayerRef.current.src);
          audioPlayerRef.current.src = '';
        }
      } catch (e) {
        console.warn('Error cleaning up audio player:', e);
      }
    }
  };
  
  // Update your component's cleanup
  useEffect(() => {
    return () => {
      cleanupAudioStream();
    };
  }, []);
  

  const connectWebSocket = async () => {
    console.log('Connecting to server...');
    const domain = activeTab.toLowerCase();
    console.log(`Connecting to server with...${domain}`);
    const ws = new WebSocket(`https://quest-api.symbl.ai/ws/${domain}/${Math.random().toString(36).substring(7)}`);
    wsRef.current = ws;

    ws.onopen = () => {
        console.log('Server connection established');
        setIsConnected(true);
        startAudioStream();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.event) {
          case 'interim':
            setCurrentInterim(data.text);
            break;
            
          case 'final':
            setMessages(prev => [...prev, { sender: "user", text: data.text }]);
            setCurrentInterim("");
            break;
            
          case 'nebula_response':
            setMessages(prev => [...prev, { sender: "agent", text: data.text }]);
            if (data.audio && !isMuted) {
                playAudio(data.audio);
              }
            break;
            
          case 'error':
            console.error('Server error:', data.error);
            break;
        }
      } catch (error) {
        console.error('Error processing message:', error);
      }
    };

    ws.onclose = () => {
      console.log('Server connection closed');
      setIsConnected(false);
      stopAudioStream();
    };

    ws.onerror = (error) => {
      console.error('Connection error:', error);
    };
  };

  const stopAudioStream = () => {
    // Stop media tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;  // Clear the reference
    }
  
    // Disconnect processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;  // Clear the reference
    }
  
    // Close AudioContext only if it's still active
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().then(() => {
        audioContextRef.current = null;  // Clear the reference
      }).catch(error => {
        console.error('Error closing AudioContext:', error);
      });
    }
  
    // Stop audio player
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      if (audioPlayerRef.current.src) {
        URL.revokeObjectURL(audioPlayerRef.current.src);
        audioPlayerRef.current.src = '';
      }
    }
  };

  const handleStartAgent = async () => {
    setIsModalOpen(false);
    await connectWebSocket();
  };

  const handleHangUp = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    stopAudioStream();
    setIsConnected(false);
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
    
    // Mute/unmute the microphone tracks
    if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach(track => {
            track.enabled = isMuted;  // Toggle track enabled state
        });
    }

    // Stop audio player if unmuting
    if (!isMuted) {
        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
        }
    }
};

// Add a useEffect to monitor mute state
useEffect(() => {
    if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach(track => {
            track.enabled = !isMuted;
        });
    }
}, [isMuted]);
  useEffect(() => {
    return () => {
      handleHangUp();
    };
  }, []);

  const DOMAIN_CONTENT = {
    healthcare: {
      title: "Healthcare Assistant Demo",
      description: "Interact with an AI Healthcare representative to schedule appointments, explain benefits, and handle claims. Experience seamless, empathetic assistance as a patient seeking support.",
      agentRole: "medical insurance representative",
      icon: Heart,
      color: '#2563EB'
    },
    fintech: {
      title: "Financial Advisory Assistant Demo",
      description: "Interact with an AI financial advisor for investment planning, loan applications, credit assessments, and banking support. Take on the role of a client seeking financial guidance.",
      agentRole: "financial advisor",
      icon: Building2,
      color: '#2563EB'
    },
    legal: {
      title: "Legal Consultation Assistant Demo",
      description: "Connect with an AI legal assistant for preliminary guidance, document review, and attorney scheduling as a client seeking legal help.",
      agentRole: "legal assistant",
      icon: Scale,
      color: '#2563EB'
    }
  };

  return (
    <div className="voice-agent-container">
      {isModalOpen && (
  <div className="modal-overlay">
    <div className="voice-ai-agent-modal-content">
      {/* <div className="domain-icon">
        {React.createElement(DOMAIN_CONTENT[activeTab.toLowerCase()].icon, {
          size: 32,
          color: DOMAIN_CONTENT[activeTab.toLowerCase()].color
        })}
      </div> */}
      {/* <h2>{DOMAIN_CONTENT[activeTab.toLowerCase()].title}</h2> */}
      <p className="domain-description">
        {DOMAIN_CONTENT[activeTab.toLowerCase()].description}
      </p>
      
      <h3>Make sure:</h3>
      
      <div className="requirement-item">
        <Volume2 size={20} />
        <span>Sound is on</span>
      </div>
      
      <div className="requirement-item">
        <Mic size={20} />
        <span>Enable microphone access</span>
      </div>

      <button className="start-button" onClick={handleStartAgent}>
        <Play size={20} />
        Talk to Voice Agent
      </button>
    </div>
  </div>
)}

      
      <div className="tabs" style={{ gap: "15px",position: 'relative', zIndex: 1001 , padding: '10px 20px',  }}>
        {CONVERSATION_TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = activeTab === type.label;
          
          return (
            <button
              key={type.id}
              onClick={() => {if (isConnected) {
                handleHangUp(); // Close existing connection
              }setActiveTab(type.label);
              setIsModalOpen(true);
            }}
              className={`tab ${isSelected ? 'active' : ''}`}
              style={{
                backgroundColor: isSelected ? '#0075FF' : '#2D3748',
                color: '#FFFFFF',
                border: `1px solid ${isSelected ? '#0075FF' : '#4A5568'}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s ease',
                padding: '12px 20px',
                fontSize: '15px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                width: '160px',
                minWidth: 'fit-content'
              }}
            >
              <Icon style={{
                width: 20,
                height: 20,
                color: '#FFFFFF',
                flexShrink: 0 // Prevents icon from shrinking
              }} />
              <span>{type.label}</span>
            </button>
          );
        })}
      </div>
      
      <div className={`chat-window-container ${isModalOpen ? 'blurred' : ''}`}>
    <MessageList 
      messages={messages} 
      currentInterim={currentInterim}     
    />
    <div className="controls" style={{ 
        display: 'flex', 
        flexDirection: isConversationEnded ? 'column' : 'row', // Change direction based on state
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
        
    }}>
        {isConversationEnded ? (
            <>
                <div style={{ 
                    color: 'black', 
                    fontSize: '16px', 
                    marginBottom: '-3px',
                    textAlign: 'center',

                }}>
                    Conversation Ended
                </div>
                <button
                    onClick={() => {
                        setIsConversationEnded(false);
                        setMessages([{
                            sender: "agent",
                            text: domainGreetings[activeTab.toLowerCase()].text
                        }]);
                        connectWebSocket();
                    }}
                    style={{
                        backgroundColor: '#FFFFFF',
                        color: '#2563EB',
                        border: '2px solid #2563EB',
                        borderRadius: '20px 0 20px 0',
                        padding: '12px 20px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        width: 'auto',
                        minWidth: '250px',
                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.1)',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        textAlign: 'center'
                    }}
                >
                    Start New Conversation
                </button>
            </>
        ) : (
            <>
                <button
                    className={`control-button hangup ${!isConnected ? 'disabled' : ''}`}
                    onClick={() => {
                        handleHangUp();
                        setIsConversationEnded(true);
                    }}
                    disabled={!isConnected}
                    style={{
                        backgroundColor: '#F44336',
                        color: '#FFFFFF',
                        borderRadius: '20px 0 20px 0',
                        padding: '10px 20px',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        minWidth: 'auto',
                    }}
                >
                    <PhoneOff style={{ color: '#FFFFFF', width: 20, height: 20 }} /> Hang Up
                </button>
                <button
                    className={`control-button mute ${isMuted ? 'active' : ''}`}
                    onClick={handleMute}
                    disabled={!isConnected}
                    style={{
                        backgroundColor: isMuted ? '#FFFFFF' : 'transparent',
                        color: '#2196F3',
                        border: '2px solid #2196F3',
                        borderRadius: '20px 0 20px 0',
                        padding: '10px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        minWidth: 'auto'
                    }}
                >
                    <MicOff style={{ color: isMuted ? '#2196F3' : '#2196F3', width: 20, height: 20 }} /> 
                    {isMuted ? 'Unmute' : 'Mute'}
                </button>
            </>
        )}
    </div>
</div>
</div>
  );
};

export default DemoVoiceAiAgent;
